import useIsSmall from "@/hooks/useIsSmall";
import {
  Accordion,
  Alert,
  Badge,
  Box,
  Button,
  CloseButton,
  Drawer,
  Group,
  Image,
  Modal,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { IconBulb, IconZoomIn } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { HelpStep, HelpTopic } from "./types";

type Props = {
  topic: HelpTopic;
  opened: boolean;
  onClose: () => void;
  /** The step the user is on; the guide opens that step and follows it. */
  activeStep?: string;
  onStartTour?: () => void;
};

/**
 * "راهنمای این صفحه": a bottom sheet on phones; on larger screens an aside the page places
 * next to its form (rendered only while open), so the user can read and fill at the same time.
 */
export default function HelpPanel({ topic, opened, onClose, activeStep, onStartTour }: Props) {
  const isSmall = useIsSmall();

  if (isSmall) {
    return (
      <Drawer
        opened={opened}
        onClose={onClose}
        position="bottom"
        radius="lg"
        title={<Text fw={700}>{topic.title}</Text>}
        // size="auto" does not size a Mantine drawer to its content; the height is set here instead.
        styles={{ content: { height: "auto", maxHeight: "88dvh" } }}
      >
        <HelpContent topic={topic} activeStep={activeStep} onStartTour={onStartTour} onDone={onClose} />
      </Drawer>
    );
  }

  if (!opened) return null;
  return (
    <Paper
      component="aside"
      aria-label={topic.title}
      withBorder
      radius="lg"
      p="md"
      w={340}
      style={{ flexShrink: 0, position: "sticky", top: 72, maxHeight: "calc(100dvh - 96px)", overflowY: "auto" }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb="xs">
        <Title order={5}>{topic.title}</Title>
        <CloseButton aria-label="بستن راهنما" onClick={onClose} />
      </Group>
      <HelpContent topic={topic} activeStep={activeStep} onStartTour={onStartTour} compact />
    </Paper>
  );
}

function HelpContent({
  topic,
  activeStep,
  onStartTour,
  onDone,
  compact = false,
}: {
  topic: HelpTopic;
  activeStep?: string;
  onStartTour?: () => void;
  onDone?: () => void;
  compact?: boolean;
}) {
  const [openStep, setOpenStep] = useState<string | null>(activeStep ?? topic.steps[0]?.key ?? null);
  const [zoomed, setZoomed] = useState<HelpStep | null>(null);

  useEffect(() => {
    if (activeStep) setOpenStep(activeStep);
  }, [activeStep]);

  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed" lh={1.8}>
        {topic.intro}
      </Text>

      <Accordion value={openStep} onChange={setOpenStep} variant="separated" radius="md" chevronPosition="right">
        {topic.steps.map((step, index) => (
          <Accordion.Item key={step.key} value={step.key}>
            <Accordion.Control
              icon={
                <ThemeIcon radius="xl" size={26} variant={step.optional ? "light" : "filled"}>
                  <Text size="xs" fw={700}>
                    {(index + 1).toLocaleString("fa-IR")}
                  </Text>
                </ThemeIcon>
              }
            >
              <Group gap={6} wrap="nowrap">
                <Text fw={700} size="sm">
                  {step.title}
                </Text>
                {step.optional && (
                  <Badge size="xs" variant="light">
                    اختیاری
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                <Text size="sm" lh={1.9}>
                  {step.body}
                </Text>
                {step.image && (
                  <UnstyledButton
                    onClick={() => setZoomed(step)}
                    aria-label={`نمایش بزرگ تصویر ${step.title}`}
                    pos="relative"
                  >
                    <Image
                      src={step.image}
                      alt={step.imageAlt ?? step.title}
                      h={compact ? 120 : 140}
                      fit="cover"
                      radius="md"
                      style={{ objectPosition: "top", border: "1px solid var(--mantine-color-gray-3)" }}
                    />
                    <Box
                      pos="absolute"
                      bottom={6}
                      left={6}
                      px={6}
                      py={2}
                      bg="rgba(255,255,255,0.9)"
                      style={{ borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <IconZoomIn size={14} />
                      <Text size="xs">بزرگ‌تر</Text>
                    </Box>
                  </UnstyledButton>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>

      {topic.tip && (
        <Alert color="yellow" variant="light" icon={<IconBulb size={18} />} p="sm">
          <Text size="sm" lh={1.8}>
            {topic.tip}
          </Text>
        </Alert>
      )}

      {(onStartTour || onDone) && (
        <Group grow>
          {onStartTour && (
            <Button variant="default" onClick={onStartTour}>
              نمایش تور کوتاه
            </Button>
          )}
          {onDone && <Button onClick={onDone}>متوجه شدم</Button>}
        </Group>
      )}

      <Modal
        opened={!!zoomed}
        onClose={() => setZoomed(null)}
        title={zoomed?.title}
        centered
        size={420}
        zIndex={2000}
      >
        {zoomed?.image && <Image src={zoomed.image} alt={zoomed.imageAlt ?? zoomed.title} radius="md" />}
      </Modal>
    </Stack>
  );
}
